import type { TFunction } from 'i18next';
import type { IOperatorAlertV1 } from '@/lib/alerts/alert.types';
import { alertView, type IAlertView } from '@/lib/alerts/alert.view';

// The ONE concrete line an alert reads as, everywhere it shows (live region, summary rows,
// the bell, the OS notification): «Cancelado H2 1821 · EZE → SCL · 22:10»,
// «Demorado H2 1821 · EZE → SCL · 22:10 → 23:05 · +55 min», «Nueva disrupción por falla
// mecánica · ARI, SCL · 1 vuelo». A disruption reads by its reason, in words — never by
// the cases auto-title. `t` is the `alerts` namespace.

/** «Cancelado» / «Nueva disrupción por falla mecánica» / «Nueva disrupción» (no reason). */
export function headline(t: TFunction, v: IAlertView): string {
  if (v.kind === 'DISRUPTION_OPENED' && v.reason) {
    return t('openedFor', { reason: t(`reason.${v.reason}`) });
  }
  return t(`kind.${v.kind}`);
}

/** «Cancelado» / «Demorado · +55 min» / «Nueva disrupción por huelga · 3 vuelos». */
export function kindLine(t: TFunction, v: IAlertView): string {
  return [
    headline(t, v),
    v.delay ? t('delay', { count: v.delay }) : '',
    v.flights ? t('flights', { count: v.flights }) : '',
  ]
    .filter(Boolean)
    .join(' · ');
}

export function alertLine(t: TFunction, alert: IOperatorAlertV1): string {
  const v = alertView(alert);
  // A disruption: what · where · how many — the time it opened is «now» (the dialog's
  // strip and the bell's «hace N min» carry it).
  if (!v.what) {
    return [headline(t, v), v.where, v.flights ? t('flights', { count: v.flights }) : '']
      .filter(Boolean)
      .join(' · ');
  }
  return [
    `${headline(t, v)} ${v.what}`,
    v.where,
    v.when,
    v.delay ? t('delay', { count: v.delay }) : '',
  ]
    .filter(Boolean)
    .join(' · ');
}

/** The disruption a flight's news opened, under it: «Se abrió una disrupción · 1 vuelo». */
export function pairedLine(t: TFunction, disruption: IOperatorAlertV1): string {
  const v = alertView(disruption);
  return [
    v.reason ? t('pairedFor', { reason: t(`reason.${v.reason}`) }) : t('paired'),
    v.flights ? t('flights', { count: v.flights }) : '',
  ]
    .filter(Boolean)
    .join(' · ');
}
